import type { Product } from "~/types/ecom/product/product.type";

let isProductDetailFetching = false;
let isProductListFetching = false;

export class ProductService {

  private customClientErrorHandler: ReturnType<typeof useNuxtApp>['$customClientErrorHandler'];

  constructor() {
    const { $customClientErrorHandler } = useNuxtApp();
    this.customClientErrorHandler = $customClientErrorHandler;
  }

  async getProductByCode(id: number): Promise<{ data: Product } | any> {
    try {

      while (isProductDetailFetching) {
        await new Promise((resolve) => setTimeout(resolve, 100)) // Polling
      }

      isProductDetailFetching = true;
      const response = await useAjaxEcom(`/products/${id}`, {
        show_notifications: false,
        show_loading_indicator: false,
        cache:true
      });
      isProductDetailFetching = false;
      return response.data;
    } catch (e) {
      isProductDetailFetching = false;
      let errorMeta = {
        productId: id,
        isProductDetailFetching,
        productServiceError: true
      }

      this.customClientErrorHandler(
          e,
          'critical',
          'Product Service Page Error - trying to fetch product by code using getProductByCode()',
          errorMeta
      );
      throw e;
    }
  }

  async getProductResponseByCode(id: number,nuxtApp = null,runtimeConfig = null): Promise<{ data: Product } | any> {
    try {

      while (isProductDetailFetching) {
        await new Promise((resolve) => setTimeout(resolve, 100)) // Polling
      }

      isProductDetailFetching = true;

      const response = await useAjaxEcom(`/products/${id}`, {
        show_notifications: false,
        show_loading_indicator: false,
        cache:true
      },nuxtApp,runtimeConfig);
      isProductDetailFetching = false;
      return response;
    } catch (e) {
      isProductDetailFetching = false;
      let errorMeta = {
        productId: id,
        isProductDetailFetching,
        productServiceError: true
      }

      customClientErrorHandler(
          e,
          'critical',
          'Product Service Page Error - trying to fetch product by code using getProductByCode()',
          errorMeta
      );
      return e;
    }
  }

  async getProducts(id: string[], cache = false,nuxtApp = null,runtimeConfig = null): Promise<Product[] | any> {
    let options = {
      method: "post",
      params: {
        products: id,
      },
      show_notifications: false,
      show_loading_indicator: false,
      cache
    }
    try {
      while (isProductListFetching) {
        await new Promise((resolve) => setTimeout(resolve, 100)) // Polling
      }

      isProductListFetching = true;
      const response = await useAjaxEcom("/products/getProducts", options,nuxtApp,runtimeConfig);
      isProductListFetching = false;
      return response.data;
    } catch (e) {
      isProductListFetching = false;
      let errorMeta = {productId: id,options, productServiceError: true}

      this.customClientErrorHandler(
          e,
          'critical',
          'Product Service Page Error - trying to fetch product using getProducts()',
          errorMeta
      );
      
      throw e;
    }
  }

  async getStocks(id: number[], branch_id?: string[]) {
    try {
      const response = await useAjaxEcom("/stock/getStock", {
        method: "post",
        params: {
          products: id,
          sites: ["WW"],
        },
        show_notifications: false,
        show_loading_indicator: false,
      });
      return response.data;
    } catch (error) {
      let errorMeta = {branchId: id, productServiceError: true}

      this.customClientErrorHandler(
          error,
          'high',
          'Product Service Page Error - trying to fetch stocks using getStocks()',
          errorMeta
      );
      throw error;
    }
  }

  async getDeliveryDetails(id: number, sites?: string) {
    try {
      if (sites) {
        //  default_sites = [ sites, ...default_sites]
      }
      const response = await useAjaxEcom(`/products/${id}/delivery`, {
        query: {
          site: sites,
        },
        show_notifications: false,
        show_loading_indicator: false,
      });
      return response.data;
    } catch (error) {
      let errorMeta = {productId: id, productServiceError: true}

      this.customClientErrorHandler(
          error,
          'low',
          'Product Service Page Error - trying to fetch delivery details using getDeliveryDetails()',
          errorMeta
      );
      throw error;
    }
  }

  async subscribeProduct(customer_id: string, productCode: string) {
    try {
      const url = `/customers/${customer_id}/stock-notifications/${productCode}`;

      let response = await useAjaxEcom(url, {
        method: "post",
      });
      
      return response;
    } catch (error) {
      let errorMeta = {productId: productCode, customerId: customer_id, productServiceError: true}

      this.customClientErrorHandler(
          error,
          'low',
          'Product Service Page Error - trying to subscribe product using subscribeProduct()',
          errorMeta
      );
      throw error;
    }
  }
  async unSubscribeProduct(customer_id: string, product_id: string) {
    try {
      const url = `/customers/${customer_id}/stock-notifications/${product_id}`;
      let response = await useAjaxEcom(url, {
        method: "delete",
      });
      return response;

    } catch (error) {
      let errorMeta = {
        customerId: customer_id,
        productId: product_id,
        productServiceError: true
      }

      this.customClientErrorHandler(
          error,
          'low',
          'Product Service Page Error - trying to unsubscribe product using unSubscribeProduct()',
          errorMeta
      );
      throw error;
    }
  }

  async getsubscribedProducts(customerId: string) { 
    const url = `/customers/${customerId}/stock-notifications`;

    try {
      if(!customerId){
        customerId = useAuthStore().user?.id;
      }
       

      let products = await useAjaxEcom(url, {
        method: "get",
      });

      return products;
    } catch (error) {
      let errorMeta = { url, customerId, productServiceError: true}

      this.customClientErrorHandler(
          error,
          'low',
          'Product Service Page Error - trying to fetch subscribed products using getsubscribedProducts()',
          errorMeta
      );
      throw error;
    }
  }
}
